import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { CustomIcon } from '../../../../UI';
import customerService from '../../../../../services/customerService';

const CustomerEmailsSection = () => {
  const { setValue, watch } = useFormContext();
  const formCustomerEmails = watch('customerEmails') || [];

  const [selectedCustomerEmails, setSelectedCustomerEmails] = useState(formCustomerEmails);
  const [customerEmailInput, setCustomerEmailInput] = useState('');
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showAllCustomerEmails, setShowAllCustomerEmails] = useState(false);
  const [customerEmailError, setCustomerEmailError] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const customerEmailInputRef = useRef(null);
  const customerSuggestionsRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(formCustomerEmails)) {
      setSelectedCustomerEmails(formCustomerEmails);
    }
  }, [formCustomerEmails]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getCustomerEmails = (customer) => {
    const customerEmails = Array.isArray(customer.emails) ? customer.emails : [];
    const primaryEmail = customer.email || '';
    return [...new Set([...customerEmails, primaryEmail].filter(Boolean))];
  };

  const searchCustomersByEmail = useCallback(async (input) => {
    if (!input || input.length < 2) {
      setCustomerSuggestions([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await customerService.searchCustomers({
        email: input.trim(),
        limit: 10
      });

      const customers = response?.data?.data || response?.data || [];

      const customerSuggestions = customers
        .flatMap((customer) => {
          const emails = getCustomerEmails(customer);
          return emails
            .filter((email) =>
              email.toLowerCase().includes(input.toLowerCase()) &&
              !selectedCustomerEmails.includes(email)
            )
            .map((email) => ({
              email,
              customer
            }));
        });

      setCustomerSuggestions(customerSuggestions.slice(0, 5));
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, [selectedCustomerEmails]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (customerEmailInput.trim().length >= 2) {
        searchCustomersByEmail(customerEmailInput);
      } else {
        setCustomerSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [customerEmailInput, searchCustomersByEmail]);

  const handleCustomerEmailInputChange = (e) => {
    const value = e.target.value;
    setCustomerEmailInput(value);
    setCustomerEmailError('');

    if (value.trim()) {
      setShowCustomerSuggestions(true);
    } else {
      setShowCustomerSuggestions(false);
      setCustomerSuggestions([]);
    }
  };

  const handleCustomerEmailInputFocus = () => {
    if (customerEmailInput.trim().length >= 2) {
      setShowCustomerSuggestions(true);
    }
  };

  const addCustomerEmail = (email) => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setCustomerEmailError('Email cannot be empty');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setCustomerEmailError('Please enter a valid email address');
      return;
    }

    if (selectedCustomerEmails.includes(trimmedEmail)) {
      setCustomerEmailError('This email is already added');
      return;
    }

    const newEmails = [...selectedCustomerEmails, trimmedEmail];
    setSelectedCustomerEmails(newEmails);
    setValue('customerEmails', newEmails, { shouldDirty: true, shouldValidate: true });

    setCustomerEmailInput('');
    setShowCustomerSuggestions(false);
    setCustomerSuggestions([]);
    setCustomerEmailError('');
  };

  const removeCustomerEmail = (emailToRemove) => {
    const newEmails = selectedCustomerEmails.filter(email => email !== emailToRemove);
    setSelectedCustomerEmails(newEmails);
    setValue('customerEmails', newEmails, { shouldDirty: true, shouldValidate: true });
  };

  const handleCustomerSuggestionClick = (suggestion) => {
    addCustomerEmail(suggestion.email);
  };

  const handleCustomerEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (customerSuggestions.length > 0) {
        handleCustomerSuggestionClick(customerSuggestions[0]);
      } else {
        addCustomerEmail(customerEmailInput);
      }
    } else if (e.key === 'Escape') {
      setShowCustomerSuggestions(false);
      setCustomerSuggestions([]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerSuggestionsRef.current && !customerSuggestionsRef.current.contains(event.target) &&
          customerEmailInputRef.current && !customerEmailInputRef.current.contains(event.target)) {
        setShowCustomerSuggestions(false);
        setCustomerSuggestions([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="customer-emails-section">
      <h3 className="section-title">Customer Emails (CC)</h3>

      <div className="email-input-container">
        <div className="email-input-wrapper">
          <input
            ref={customerEmailInputRef}
            type="email"
            value={customerEmailInput}
            onChange={handleCustomerEmailInputChange}
            onFocus={handleCustomerEmailInputFocus}
            onKeyPress={handleCustomerEmailKeyPress}
            placeholder="Add email manually or search by email..."
            className={`email-input ${customerEmailError ? 'error' : ''}`}
          />

          {isSearching && <span className="search-loading-indicator">Searching...</span>}

          {showCustomerSuggestions && customerSuggestions.length > 0 && (
            <div ref={customerSuggestionsRef} className="email-suggestions">
              {customerSuggestions.map((suggestion, index) => (
                <div
                  key={`${suggestion.email}-${index}`}
                  className="suggestion-item"
                  onClick={() => handleCustomerSuggestionClick(suggestion)}
                >
                  <div className="suggestion-content">
                    <div className="suggestion-email">
                      <CustomIcon name="person" size={16} />
                      <span>{suggestion.email}</span>
                    </div>
                    {suggestion.customer?.companyName && (
                      <div className="suggestion-company">
                        <CustomIcon name="building" size={14} />
                        <span>{suggestion.customer.companyName}</span>
                        {suggestion.customer.customerAddress?.city && (
                          <span className="suggestion-location">
                            • {suggestion.customer.customerAddress.city}, {suggestion.customer.customerAddress.state}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {showCustomerSuggestions && customerSuggestions.length === 0 && customerEmailInput.length >= 2 && (
            <div ref={customerSuggestionsRef} className="email-suggestions">
              <div className="no-results-message">
                No customers found. You can still add this email manually.
              </div>
            </div>
          )}
        </div>

        {customerEmailError && (
          <span className="email-error-message">{customerEmailError}</span>
        )}
      </div>

      <div className="email-actions">
        {selectedCustomerEmails.length > 0 && (
          <button
            type="button"
            className="see-all-link"
            onClick={() => setShowAllCustomerEmails(!showAllCustomerEmails)}
          >
            {showAllCustomerEmails ? 'Hide added' : `Show added (${selectedCustomerEmails.length})`}
          </button>
        )}

        <button
          type="button"
          className="add-email-btn"
          onClick={() => addCustomerEmail(customerEmailInput)}
          disabled={!customerEmailInput.trim()}
        >
          <CustomIcon name="plus" size={16} />
          Add Email
        </button>
      </div>

      {showAllCustomerEmails && selectedCustomerEmails.length > 0 && (
        <div className="all-emails-list">
          <h4 className="all-emails-title">Added emails:</h4>
          <div className="all-emails-grid">
            {selectedCustomerEmails.map((email, index) => (
              <div
                key={`${email}-${index}`}
                className="available-email-item"
              >
                <CustomIcon name="person" size={16} />
                <span>{email}</span>
                <button
                  type="button"
                  className="remove-email-small"
                  onClick={() => removeCustomerEmail(email)}
                  title="Remove email"
                >
                  <CustomIcon name="close" size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerEmailsSection;
